import React, { useEffect, useState } from "react"
import { Card, CardBody, Col, Container, Row, Spinner } from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"
import { get } from "helpers/api_helper"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  saGetAllTenants,
  saGetAllPlans,
  saGetAllSubscriptions,
  saGetAllInvoices,
} from "store/actions/superAdminAction"
import { ROUTES } from "constants/routes"
import isPermissionEnabled from "common/isPermissionEnabled"
import { getFirstAccessibleRoute } from "utils/accessibleRoutes"

const StatCard = ({ title, value, hint, to, icon, enabled = true }) => {
  const body = (
    <Card className="h-100 border shadow-sm">
      <CardBody>
        <div className="d-flex align-items-start justify-content-between gap-2">
          <div className="flex-grow-1 overflow-hidden">
            <p className="text-muted mb-1 fw-medium">{title}</p>
            <h4 className="mb-0">{value}</h4>
            {hint ? <p className="text-muted small mb-0 mt-2">{hint}</p> : null}
          </div>
          <div className="avatar-sm flex-shrink-0">
            <span className="avatar-title rounded bg-primary bg-opacity-10">
              <i className={`${icon} font-size-24 text-primary`} />
            </span>
          </div>
        </div>
      </CardBody>
    </Card>
  )

  if (!enabled || !to) {
    return <Col md={6} xl={3} className="mb-4">{body}</Col>
  }

  return (
    <Col md={6} xl={3} className="mb-4">
      <Link to={to} className="text-reset text-decoration-none">
        {body}
      </Link>
    </Col>
  )
}

/**
 * Platform operator home — high-level counts across tenants, plans, and billing.
 */
const SuperAdminDashboard = () => {
  const { t } = useTranslation()
  document.title = t("Dashboard | Super Admin")

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { userRole, accountType, accountScope } = useSelector(state => state.Login)
  const isSuperAdminSession =
    accountScope === "super_admin" || accountType === "superadmin"
  const canViewDashboard = isPermissionEnabled(
    userRole,
    101,
    "sa_dashboard_1",
    accountType,
    accountScope
  )
  const canViewTenants = isPermissionEnabled(
    userRole,
    104,
    "sa_tenants_1",
    accountType,
    accountScope
  )
  const canCreateTenants = isPermissionEnabled(
    userRole,
    104,
    "sa_tenants_2",
    accountType,
    accountScope
  )
  const canViewPlans = isPermissionEnabled(
    userRole,
    105,
    "sa_plans_1",
    accountType,
    accountScope
  )
  const canCreatePlans = isPermissionEnabled(
    userRole,
    105,
    "sa_plans_2",
    accountType,
    accountScope
  )
  const canViewBilling = isPermissionEnabled(
    userRole,
    106,
    "sa_billing_1",
    accountType,
    accountScope
  )
  const {
    tenantsLoading,
    tenantsTotalCount,
    plansLoading,
    plansTotalCount,
    subscriptionsLoading,
    subscriptionsTotalCount,
    invoicesLoading,
    invoicesTotalCount,
  } = useSelector(state => state.SuperAdmin)

  const [bootstrapping, setBootstrapping] = useState(true)
  const [activeTenantsCount, setActiveTenantsCount] = useState(null)

  useEffect(() => {
    if (!isSuperAdminSession) {
      navigate(
        getFirstAccessibleRoute({ userRole, accountType, accountScope }),
        { replace: true }
      )
      return
    }
    if (!canViewDashboard) {
      navigate(
        getFirstAccessibleRoute({ userRole, accountType, accountScope }),
        { replace: true, state: { accessDenied: true } }
      )
    }
  }, [isSuperAdminSession, canViewDashboard, navigate, userRole, accountType, accountScope])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const requests = []
        if (canViewTenants) {
          requests.push(dispatch(saGetAllTenants({ page: 1, limit: 1 })).unwrap())
        }
        if (canViewPlans) {
          requests.push(dispatch(saGetAllPlans({ page: 1, limit: 1 })).unwrap())
        }
        if (canViewBilling) {
          requests.push(dispatch(saGetAllSubscriptions({ page: 1, limit: 1 })).unwrap())
          requests.push(dispatch(saGetAllInvoices({ page: 1, limit: 1 })).unwrap())
        }
        if (requests.length > 0) {
          await Promise.allSettled(requests)
        }
      } catch {
        /* counts fall back to slice or em dash */
      }
      try {
        if (canViewTenants) {
          const activeRes = await get("superadmin/tenants/all", {
            params: { page: 1, limit: 1, status: "active" },
          })
          if (!cancelled && activeRes?.totalCount != null) {
            setActiveTenantsCount(activeRes.totalCount)
          }
        } else if (!cancelled) {
          setActiveTenantsCount(0)
        }
      } catch {
        /* optional metric */
      }
      if (!cancelled) setBootstrapping(false)
    }
    load()
    return () => {
      cancelled = true
    }
  }, [dispatch, canViewTenants, canViewPlans, canViewBilling])

  const loading =
    bootstrapping ||
    tenantsLoading ||
    plansLoading ||
    subscriptionsLoading ||
    invoicesLoading

  const fmt = n => (n == null || Number.isNaN(n) ? "—" : n.toLocaleString())

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs />
          <Row className="align-items-center mb-4">
            <Col>
              <h4 className="mb-1">{t("Platform overview")}</h4>
              <p className="text-muted mb-0">
                {t("Quick snapshot of tenants, catalog, and billing")}
              </p>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <Row>
              <StatCard
                title={t("Tenants")}
                value={fmt(canViewTenants ? tenantsTotalCount : 0)}
                hint={
                  activeTenantsCount != null
                    ? `${t("Active")}: ${fmt(activeTenantsCount)}`
                    : null
                }
                to={ROUTES.SA_TENANTS}
                icon="bx bx-buildings"
                enabled={canViewTenants}
              />
              <StatCard
                title={t("Subscription plans")}
                value={fmt(canViewPlans ? plansTotalCount : 0)}
                hint={t("Billable products")}
                to={ROUTES.SA_PLANS}
                icon="bx bx-package"
                enabled={canViewPlans}
              />
              <StatCard
                title={t("Subscriptions")}
                value={fmt(canViewBilling ? subscriptionsTotalCount : 0)}
                hint={t("Across all tenants")}
                to={ROUTES.SA_SUBSCRIPTIONS}
                icon="bx bx-receipt"
                enabled={canViewBilling}
              />
              <StatCard
                title={t("Invoices")}
                value={fmt(canViewBilling ? invoicesTotalCount : 0)}
                hint={t("All records")}
                to={ROUTES.SA_INVOICES}
                icon="bx bx-file"
                enabled={canViewBilling}
              />
            </Row>
          )}

          <Row className="mt-2">
            <Col lg={8}>
              <Card className="border shadow-sm">
                <CardBody>
                  <h5 className="card-title">{t("Next steps")}</h5>
                  <ul className="text-muted mb-0 ps-3">
                    {canCreateTenants && (
                    <li className="mb-2">
                      <Link to={ROUTES.SA_TENANT_ADD}>{t("Onboard a new tenant")}</Link>
                      {canCreatePlans ? (
                        <>
                          {" · "}
                          <Link to={ROUTES.SA_PLAN_ADD}>{t("Create a plan")}</Link>
                        </>
                      ) : null}
                    </li>
                    )}
                    {!canCreateTenants && canCreatePlans && (
                    <li className="mb-2">
                      <Link to={ROUTES.SA_PLAN_ADD}>{t("Create a plan")}</Link>
                    </li>
                    )}
                    {canViewBilling && (
                    <li>
                      <Link to={ROUTES.SA_SUBSCRIPTIONS}>
                        {t("Review subscription health")}
                      </Link>
                      {" · "}
                      <Link to={ROUTES.SA_INVOICES}>{t("Open invoices")}</Link>
                    </li>
                    )}
                  </ul>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default SuperAdminDashboard
